/**
 * Full-width review mode overlay for HITL proposal review (Phase 32.2).
 *
 * Layout:
 *   - Left pane: canonical document (read-only, rendered markdown)
 *   - Right pane: proposed sections as diff cards (SectionDiff)
 *   - Bottom bar (sticky): Accept all | Reject all | Exit
 *
 * Accept/reject actions update backend state in real time (optimistic UI).
 * Accept-all triggers canonical merge on the backend; reject-all deletes
 * the proposed file. Exiting without saving preserves current state.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import SectionDiff from "./SectionDiff.jsx";
import {
  acceptSection,
  rejectSection,
  acceptAll,
  rejectAll,
  getProposal,
} from "../../services/proposals.js";

/**
 * Extract text for a specific H2 section from markdown.
 * Returns everything from `## {heading}` up to the next `## ` or end of string.
 */
function extractSectionText(markdown, heading) {
  if (!markdown || !heading) return "";
  const lines = markdown.split("\n");
  let capturing = false;
  const result = [];
  for (const line of lines) {
    if (line.match(/^## /)) {
      if (capturing) break;
      if (line.replace(/^## /, "").trim() === heading) {
        capturing = true;
        result.push(line);
      }
    } else if (capturing) {
      result.push(line);
    }
  }
  return result.join("\n").trim();
}

/** Check if the proposed markdown is missing a heading that should exist. */
function hasHeadingChanged(canonicalMd, proposedMd, sectionId) {
  if (!proposedMd) return false;
  const re = new RegExp(`^## ${sectionId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*$`, "m");
  return !re.test(proposedMd);
}

export default function ReviewMode({
  projectId,
  proposal: initialProposal,
  canonical,
  onClose,
  onProposalUpdate,
  onError,
}) {
  const [proposal, setProposal] = useState(initialProposal);
  const [busy, setBusy] = useState(false);
  const overlayRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  // Sync if parent updates the proposal
  useEffect(() => {
    setProposal(initialProposal);
  }, [initialProposal]);

  const proposedFilename = proposal?.sk || initialProposal?.sk;

  const hasPending = proposal?.sections?.some((s) => s.status === "pending");

  // Escape key closes
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Lock body scroll while overlay is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const updateProposal = useCallback((updated) => {
    setProposal(updated);
    onProposalUpdate?.(updated);
  }, [onProposalUpdate]);

  const handleAcceptSection = useCallback(async (sectionId) => {
    setBusy(true);
    try {
      setProposal((prev) => ({
        ...prev,
        sections: prev.sections.map((s) =>
          s.sectionId === sectionId ? { ...s, status: "accepted" } : s,
        ),
      }));
      const updated = await acceptSection(projectId, proposedFilename, sectionId);
      updateProposal(updated);
    } catch (err) {
      setProposal((prev) => ({
        ...prev,
        sections: prev.sections.map((s) =>
          s.sectionId === sectionId ? { ...s, status: "pending" } : s,
        ),
      }));
      onError?.(`Failed to accept section: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }, [projectId, proposedFilename, updateProposal, onError]);

  const handleRejectSection = useCallback(async (sectionId, feedback) => {
    setBusy(true);
    try {
      setProposal((prev) => ({
        ...prev,
        sections: prev.sections.map((s) =>
          s.sectionId === sectionId ? { ...s, status: "rejected" } : s,
        ),
      }));
      const updated = await rejectSection(projectId, proposedFilename, sectionId, feedback);
      updateProposal(updated);

      if (feedback) {
        pollForRegen(sectionId);
      }
    } catch (err) {
      setProposal((prev) => ({
        ...prev,
        sections: prev.sections.map((s) =>
          s.sectionId === sectionId ? { ...s, status: "pending" } : s,
        ),
      }));
      onError?.(`Failed to reject section: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }, [projectId, proposedFilename, updateProposal, onError, pollForRegen]);

  /** Poll the proposal until the rejected section's status returns to pending. */
  const pollForRegen = useCallback(async (sectionId) => {
    const maxAttempts = 30; // 60s at 2s intervals
    for (let i = 0; i < maxAttempts; i++) {
      if (!mountedRef.current) return;
      await new Promise((r) => setTimeout(r, 2000));
      if (!mountedRef.current) return;
      try {
        const fresh = await getProposal(projectId, proposedFilename);
        if (!mountedRef.current) return;
        const sec = fresh.sections?.find((s) => s.sectionId === sectionId);
        if (sec && sec.status === "pending") {
          updateProposal(fresh);
          return;
        }
      } catch {
        // Network blip — keep polling
      }
    }
  }, [projectId, proposedFilename, updateProposal]);

  const handleAcceptAll = useCallback(async () => {
    setBusy(true);
    try {
      const updated = await acceptAll(projectId, proposedFilename);
      updateProposal(updated);
    } catch (err) {
      onError?.(`Failed to accept all: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }, [projectId, proposedFilename, updateProposal, onError]);

  const handleRejectAll = useCallback(async () => {
    setBusy(true);
    try {
      const updated = await rejectAll(projectId, proposedFilename);
      updateProposal(updated);
    } catch (err) {
      onError?.(`Failed to reject all: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }, [projectId, proposedFilename, updateProposal, onError]);

  return (
    <div className="review-overlay" ref={overlayRef}>
      <div className="review-header">
        <h2>Review Proposed Changes</h2>
        <span className="review-filename">{proposedFilename}</span>
      </div>

      <div className="review-columns">
        {/* Left: canonical document */}
        <div className="review-col review-col-canonical">
          <div className="review-col-label">Current (canonical)</div>
          <div className="review-col-content">
            <ReactMarkdown>{canonical || "*No canonical document yet*"}</ReactMarkdown>
          </div>
        </div>

        {/* Right: proposed section cards */}
        <div className="review-col review-col-proposed">
          <div className="review-col-label">Proposed changes</div>
          <div className="review-col-content">
            {proposal?.sections?.map((section) => (
              <SectionDiff
                key={section.sectionId}
                section={section}
                canonicalText={extractSectionText(canonical, section.sectionId)}
                proposedText={extractSectionText(proposal.proposed, section.sectionId)}
                headingChanged={hasHeadingChanged(canonical, proposal.proposed, section.sectionId)}
                onAccept={handleAcceptSection}
                onReject={handleRejectSection}
                busy={busy}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom sticky bar */}
      <div className="review-bottom-bar">
        {hasPending && (
          <>
            <button
              type="button"
              className="btn-primary review-btn-accept-all"
              onClick={handleAcceptAll}
              disabled={busy}
            >
              Accept all remaining
            </button>
            <button
              type="button"
              className="btn-secondary review-btn-reject-all"
              onClick={handleRejectAll}
              disabled={busy}
            >
              Reject all remaining
            </button>
          </>
        )}
        <button
          type="button"
          className="btn-secondary review-btn-exit"
          onClick={onClose}
        >
          Exit
        </button>
      </div>
    </div>
  );
}
